import {
  ConfirmDialog,
  WbButton,
  WbTable,
  WbResponsiveTableToolbar,
  createActionsColumn,
} from '@agilelab/plugin-wb-platform';
import { ErrorPanel } from '@backstage/core-components';
import { IconButton, makeStyles } from '@material-ui/core';
import AddCircleIcon from '@material-ui/icons/AddCircle';
import CancelIcon from '@material-ui/icons/Cancel';
import { FC, useEffect, useMemo } from 'react';
import { useOverviewContext } from './OverviewContext';
import { useRolePermissionsTableContext } from './RolePermissionsTableContext';
import { PermissionEntity } from './ManageRolePermissionsDrawer';

const useStyles = makeStyles(
  () => ({
    container: {
      height: '100%',
    },
    header: {
      display: 'flex',
      justifyContent: 'space-between',
    },
    tableContainer: {
      flex: '1 1 auto',
    },
  }),
  { name: 'RolePermissionsTable' },
);

export const DeleteConfirmDialog = () => {
  const {
    selectedDeleteItems,
    setSelectedDeleteItems,
    removePermissions,
    setOpenConfirmDialog,
    openConfirmDialog,
  } = useRolePermissionsTableContext();
  return (
    <ConfirmDialog
      title="Remove permission"
      description={`Are you sure you want to remove ${
        selectedDeleteItems.length
      } ${
        selectedDeleteItems.length === 1 ? 'permission' : 'permissions'
      } from this role?`}
      onConfirm={() => {
        removePermissions(selectedDeleteItems.map(i => i.id));
        setSelectedDeleteItems([]);
        setOpenConfirmDialog(false);
      }}
      open={openConfirmDialog}
      onClose={() => setOpenConfirmDialog(false)}
      confirmButtonText="Remove"
    />
  );
};

const RolePermissionActions: FC = () => {
  const {
    selectedDeleteItems,
    setOpenConfirmDialog,
    setOpenManageDrawer,
    permissionsLoaded,
    setSelectedPermissions,
  } = useRolePermissionsTableContext();
  const { canEditRolePermission } = useOverviewContext();

  return (
    <>
      {canEditRolePermission && (
        <>
          {!!selectedDeleteItems?.length ? (
            <WbButton
              label="Remove selected"
              startIcon={<CancelIcon />}
              onClick={() => setOpenConfirmDialog(true)}
              color="secondary"
              size="small"
              variant="outlined"
            />
          ) : (
            <WbButton
              label="Manage Permissions"
              icon={<AddCircleIcon />}
              onClick={() => {
                setOpenManageDrawer(true);
                setSelectedPermissions(
                  permissionsLoaded.value?.permissions ?? [],
                );
              }}
              color="primary"
              size="small"
              // Prevent the drawer to be opened while synching current role permissions
              loading={permissionsLoaded.loading}
              disabled={permissionsLoaded.loading}
            />
          )}
        </>
      )}
    </>
  );
};

export const RolePermissionCardHeader: FC = () => {
  const { selectedDeleteItems, setSelectedDeleteItems, filters, setFilters } =
    useRolePermissionsTableContext();

  return (
    <WbResponsiveTableToolbar
      searchProps={{
        value: filters.text,
        onChange: search => {
          setFilters({ text: search });
        },
      }}
      onClear={() => setFilters({})}
      selectedProps={{
        selectedCount: selectedDeleteItems?.length ?? 0,
        selectedLabel: 'Permission',
        onClearSelection: () => setSelectedDeleteItems([]),
      }}
    >
      <WbResponsiveTableToolbar.Actions>
        <RolePermissionActions />
      </WbResponsiveTableToolbar.Actions>
    </WbResponsiveTableToolbar>
  );
};

export const RolePermissionsTable: FC = ({}) => {
  const classes = useStyles();
  const {
    defaultColumns,
    permissionsLoaded,
    loadPermissions,
    filteredPermissions,
    loadFilteredPermissions,
    selectedDeleteItems,
    setSelectedDeleteItems,
    setOpenConfirmDialog,
  } = useRolePermissionsTableContext();

  const { canEditRolePermission } = useOverviewContext();

  useEffect(() => {
    loadPermissions();
  }, [loadPermissions]);

  useEffect(() => {
    loadFilteredPermissions();
  }, [loadFilteredPermissions]);

  const columns = useMemo(() => {
    const cols = [...defaultColumns];

    if (canEditRolePermission) {
      createActionsColumn<PermissionEntity>({
        renderActions: field => (
          <IconButton
            aria-label="remove"
            size="small"
            onClick={() => {
              setSelectedDeleteItems([field]);
              setOpenConfirmDialog?.(true);
            }}
          >
            <CancelIcon />
          </IconButton>
        ),
      });
    }
    return cols;
  }, [
    defaultColumns,
    setOpenConfirmDialog,
    setSelectedDeleteItems,
    canEditRolePermission,
  ]);

  if (permissionsLoaded.error)
    return (
      <ErrorPanel
        error={
          permissionsLoaded.error instanceof Error
            ? permissionsLoaded.error
            : new Error('Something went wrong')
        }
      />
    );
  const getRowId = (r: PermissionEntity) => r.id;

  return (
    <WbTable<PermissionEntity>
      selection="multiple"
      stickyHeader
      classNames={{ container: classes.tableContainer }}
      selectedItems={selectedDeleteItems}
      setSelectedItems={setSelectedDeleteItems}
      components={{
        tableLoader: {
          loading: filteredPermissions.loading,
        },
        tableContent: {
          getRowId,
          columns,
          rows: filteredPermissions.value?.permissions,
        },
      }}
    />
  );
};
